// SVG Gantt renderer for the Plan page. Pure React, no external chart deps.
// Handles:
//   • date-axis header (day / week / month zoom)
//   • task bars with % complete overlay
//   • milestone diamonds
//   • dependency arrows (finish-to-start default)
//   • drag horizontally to reschedule (shifts start, keeps duration)

import React, { useEffect, useMemo, useRef, useState } from "react";

// One row height matches the table row height so left table + gantt line up.
const ROW_H = 32;
const HEADER_H = 48;
const BAR_INSET = 6;
const BAR_H = ROW_H - BAR_INSET * 2;

const ZOOM = {
  day:   { colW: 26, unit: "day",   label: (d) => d.toLocaleDateString("en-US", { day: "2-digit" }) },
  week:  { colW: 34, unit: "week",  label: (d) => `W${weekOfYear(d)}` },
  month: { colW: 44, unit: "month", label: (d) => d.toLocaleDateString("en-US", { month: "short" }) },
};

function weekOfYear(d) {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

function toDate(iso) {
  if (!iso) return null;
  const parts = String(iso).slice(0, 10).split("-").map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}

function fromDate(d) {
  return d.toISOString().slice(0, 10);
}

function diffDays(a, b) {
  return Math.round((b - a) / 86400000);
}

function addDays(d, n) {
  const c = new Date(d.getTime());
  c.setUTCDate(c.getUTCDate() + n);
  return c;
}

export default function GanttChart({
  tasks, zoom = "day", scrollTop = 0,
  onRowClick, onReschedule, onResize, onConnect,
  showCritical = true, selectedId,
}) {
  const cfg = ZOOM[zoom] || ZOOM.day;
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  const [connect, setConnect] = useState(null);
  const dragCleanupRef = useRef(null);
  // Build a lookup of critical ids from computed flag on each task.
  const criticalIds = useMemo(() => {
    const s = new Set();
    (tasks || []).forEach((t) => { if (t.is_critical) s.add(t.id); });
    return s;
  }, [tasks]);

  // Detach any active drag listeners when the component unmounts.
  useEffect(() => () => {
    if (dragCleanupRef.current) dragCleanupRef.current();
  }, []);

  // Compute date range from task min-start to max-finish, +padding.
  // Prefer computed_* from CPM when available, otherwise fall back to
  // explicit start/finish.
  const [rangeStart, days] = useMemo(() => {
    let min = null, max = null;
    tasks.forEach((t) => {
      const s = toDate(t.computed_start || t.start);
      const f = toDate(t.computed_finish || t.finish) || s;
      if (s && (!min || s < min)) min = s;
      if (f && (!max || f > max)) max = f;
    });
    if (!min) {
      const today = new Date();
      min = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    }
    if (!max) max = addDays(min, 30);
    // Pad 3 days before and after
    const start = addDays(min, -3);
    const totalDays = diffDays(start, addDays(max, 3)) + 1;
    return [start, Math.max(30, totalDays)];
  }, [tasks]);

  const width = days * cfg.colW;
  const height = HEADER_H + tasks.length * ROW_H;

  // X coordinate for a given date
  const xFor = (d) => diffDays(rangeStart, d) * cfg.colW;

  // Snap a pixel-x to whole day
  const daysFromPx = (px) => Math.round(px / cfg.colW);

  // Task id -> row index (for arrows)
  const rowById = useMemo(() => {
    const m = {};
    tasks.forEach((t, i) => { m[t.id] = i; });
    return m;
  }, [tasks]);

  // Header ticks
  const headerCells = [];
  for (let i = 0; i < days; i++) {
    const d = addDays(rangeStart, i);
    const x = i * cfg.colW;
    const isMonthStart = d.getUTCDate() === 1;
    const isWeekStart = d.getUTCDay() === 1;
    const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
    // Weekend shading
    if (isWeekend) {
      headerCells.push(
        <rect
          key={`we-${i}`}
          x={x} y={HEADER_H} width={cfg.colW} height={height - HEADER_H}
          fill="#F8FAFC"
        />,
      );
    }
    // Vertical gridlines (softer for regular days, harder for month boundaries)
    headerCells.push(
      <line
        key={`vl-${i}`}
        x1={x} y1={HEADER_H} x2={x} y2={height}
        stroke={isMonthStart ? "#CBD5E1" : "#F1F5F9"}
        strokeWidth={isMonthStart ? 1 : 0.5}
      />,
    );
    // Header labels — day number always; month/week label at boundaries
    if (zoom === "day") {
      headerCells.push(
        <text key={`d-${i}`} x={x + cfg.colW / 2} y={40} fontSize="10" textAnchor="middle" fill={isWeekend ? "#94A3B8" : "#334155"}>
          {d.getUTCDate()}
        </text>,
      );
      if (isMonthStart || i === 0) {
        headerCells.push(
          <text key={`m-${i}`} x={x + 3} y={18} fontSize="10.5" fontWeight="600" fill="#0F172A">
            {d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          </text>,
        );
      }
    } else if (zoom === "week" && isWeekStart) {
      headerCells.push(
        <text key={`w-${i}`} x={x + 3} y={40} fontSize="10" fill="#334155">
          {`W${weekOfYear(d)}`}
        </text>,
      );
      if (isMonthStart || i === 0) {
        headerCells.push(
          <text key={`mw-${i}`} x={x + 3} y={18} fontSize="10.5" fontWeight="600" fill="#0F172A">
            {d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          </text>,
        );
      }
    } else if (zoom === "month" && isMonthStart) {
      headerCells.push(
        <text key={`m-${i}`} x={x + 3} y={30} fontSize="11" fontWeight="600" fill="#0F172A">
          {d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
        </text>,
      );
    }
  }

  // Today marker
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const todayX = xFor(todayUTC);
  const todayVisible = todayX >= 0 && todayX <= width;

  // Row backgrounds & bars
  const rows = tasks.map((t, idx) => {
    const y = HEADER_H + idx * ROW_H;
    const s = toDate(t.computed_start || t.start);
    const f = toDate(t.computed_finish || t.finish) || s;
    const isSel = t.id === selectedId;
    const isCrit = showCritical && criticalIds.has(t.id);

    const rowBg = (
      <rect
        key={`bg-${t.id}`}
        x={0} y={y} width={width} height={ROW_H}
        fill={isSel ? "rgba(56,126,230,0.06)" : "transparent"}
        onClick={() => onRowClick && onRowClick(t)}
      />
    );

    if (!s || !f) return rowBg;

    const barX = xFor(s);
    const barW = Math.max(cfg.colW, (diffDays(s, f) + 1) * cfg.colW);
    const barY = y + BAR_INSET;
    const pct = Math.max(0, Math.min(100, t.percent_complete || 0));

    if (t.is_milestone) {
      // Diamond
      const cx = barX + cfg.colW / 2;
      const cy = y + ROW_H / 2;
      const sz = ROW_H / 3;
      return (
        <g
          key={t.id}
          data-testid={`gantt-bar-${t.id}`}
          className="cursor-pointer"
          onClick={() => onRowClick && onRowClick(t)}
        >
          {rowBg}
          <polygon
            points={`${cx},${cy - sz} ${cx + sz},${cy} ${cx},${cy + sz} ${cx - sz},${cy}`}
            fill="#F97316"
            stroke="#C2410C"
            strokeWidth={1.5}
          />
          <title>{t.name} · {t.start}</title>
        </g>
      );
    }

    const dragging = drag && drag.id === t.id;
    const resizing = resize && resize.id === t.id;
    const displayX = dragging ? barX + drag.deltaPx : barX;
    const displayW = resizing ? Math.max(cfg.colW, barW + resize.deltaPx) : barW;
    // Bar color: summary tasks (has children) shown flat dark; leaves shown blue.
    const hasChildren = tasks.some((c) => c.parent_id === t.id);
    const fill = hasChildren ? "#0F172A" : (isCrit ? "#EF4444" : "#387EE6");
    const doneFill = hasChildren ? "#334155" : (isCrit ? "#B91C1C" : "#22C55E");

    return (
      <g key={t.id} data-testid={`gantt-bar-${t.id}`}>
        {rowBg}
        <rect
          x={displayX} y={barY}
          width={displayW} height={BAR_H}
          rx={4} ry={4}
          fill={fill}
          onMouseDown={(e) => {
            if (hasChildren) return; // don't allow drag of summary tasks
            const startX = e.clientX;
            setDrag({ id: t.id, startX, deltaPx: 0 });
            const move = (ev) => {
              setDrag((d) => d ? { ...d, deltaPx: ev.clientX - startX } : d);
            };
            const cleanup = () => {
              window.removeEventListener("mousemove", move);
              window.removeEventListener("mouseup", up);
              dragCleanupRef.current = null;
            };
            const up = (ev) => {
              cleanup();
              const dx = ev.clientX - startX;
              const shift = daysFromPx(dx);
              if (shift !== 0) {
                const newStart = fromDate(addDays(s, shift));
                const newFinish = fromDate(addDays(f, shift));
                onReschedule && onReschedule(t, { start: newStart, finish: newFinish });
              }
              setDrag(null);
            };
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", up);
            dragCleanupRef.current = cleanup;
          }}
          onClick={() => onRowClick && onRowClick(t)}
          className={hasChildren ? "cursor-pointer" : "cursor-grab"}
          style={{ transition: dragging || resizing ? "none" : "x 120ms" }}
        />
        {/* Progress overlay */}
        {pct > 0 ? (
          <rect
            x={displayX} y={barY}
            width={displayW * (pct / 100)} height={BAR_H}
            rx={4} ry={4}
            fill={doneFill}
            opacity={0.85}
            pointerEvents="none"
          />
        ) : null}
        {/* Resize handle — thin strip on right edge of leaf bars */}
        {!hasChildren && onResize ? (
          <rect
            x={displayX + displayW - 4} y={barY}
            width={6} height={BAR_H}
            fill="transparent"
            className="cursor-ew-resize"
            data-testid={`gantt-resize-${t.id}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              const startX = e.clientX;
              setResize({ id: t.id, startX, deltaPx: 0 });
              const move = (ev) => setResize((r) => r ? { ...r, deltaPx: ev.clientX - startX } : r);
              const up = (ev) => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
                const shift = daysFromPx(ev.clientX - startX);
                if (shift !== 0) {
                  const newFinish = fromDate(addDays(f, shift));
                  const startIso = fromDate(s);
                  const d1 = s; const d2 = addDays(f, shift);
                  if (d2 >= d1) onResize(t, { start: startIso, finish: newFinish });
                }
                setResize(null);
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            }}
          />
        ) : null}
        {/* Connect handle — small dot just past right edge for drawing FS deps */}
        {!hasChildren && onConnect ? (
          <circle
            cx={displayX + displayW + 6} cy={barY + BAR_H / 2}
            r={4}
            fill="#94A3B8"
            stroke="white"
            strokeWidth={1.5}
            data-testid={`gantt-connect-${t.id}`}
            className="cursor-crosshair opacity-50 hover:opacity-100 transition-opacity"
            onMouseDown={(e) => {
              e.stopPropagation();
              const rect = svgRef.current?.getBoundingClientRect();
              const startX = e.clientX;
              const startY = e.clientY;
              const originX = displayX + displayW;
              const originY = barY + BAR_H / 2;
              setConnect({ fromId: t.id, x1: originX, y1: originY, x2: originX, y2: originY });
              const move = (ev) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                setConnect((c) => c ? { ...c, x2: originX + dx, y2: originY + dy } : c);
              };
              const up = (ev) => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
                // Find target task under cursor. Simplest: hit-test by row.
                if (rect) {
                  const yInSvg = ev.clientY - rect.top;
                  const targetIdx = Math.floor((yInSvg - HEADER_H) / ROW_H);
                  if (targetIdx >= 0 && targetIdx < tasks.length) {
                    const target = tasks[targetIdx];
                    if (target && target.id !== t.id) {
                      onConnect(t, target);
                    }
                  }
                }
                setConnect(null);
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            }}
          />
        ) : null}
        <text
          x={displayX + displayW + 16} y={barY + BAR_H - 3}
          fontSize="11" fill="#334155"
          pointerEvents="none"
        >
          {t.name}{pct > 0 ? ` · ${pct}%` : ""}
        </text>
        <title>{`${t.name}\n${t.start} → ${t.finish} · ${t.duration_days}d · ${pct}%`}</title>
      </g>
    );
  });

  // Dependency arrows: FS drawn from predecessor finish → successor start,
  // SS from pred start → succ start, FF from pred finish → succ finish,
  // SF from pred start → succ finish.
  const arrows = [];
  tasks.forEach((t, idx) => {
    (t.predecessors || []).forEach((p, pi) => {
      const src = tasks.find((x) => x.id === p.task_id);
      if (!src) return;
      const srcIdx = rowById[src.id];
      if (srcIdx === undefined) return;
      const sStart = toDate(src.computed_start || src.start);
      const sFin = toDate(src.computed_finish || src.finish) || sStart;
      const tStart = toDate(t.computed_start || t.start);
      const tFin = toDate(t.computed_finish || t.finish) || tStart;
      const pType = (p.type || "FS").toUpperCase();
      let fromDateAxis, toDateAxis;
      if (pType === "FS") { fromDateAxis = addDays(sFin, 1); toDateAxis = tStart; }
      else if (pType === "SS") { fromDateAxis = sStart; toDateAxis = tStart; }
      else if (pType === "FF") { fromDateAxis = sFin; toDateAxis = tFin; }
      else { fromDateAxis = sStart; toDateAxis = tFin; }
      if (!fromDateAxis || !toDateAxis) return;
      const x1 = xFor(fromDateAxis);
      const y1 = HEADER_H + srcIdx * ROW_H + ROW_H / 2;
      const x2 = xFor(toDateAxis);
      const y2 = HEADER_H + idx * ROW_H + ROW_H / 2;
      const midX = Math.max(x1 + 6, x2 - 6);
      const d = `M ${x1} ${y1} H ${midX} V ${y2} H ${x2 - 2}`;
      arrows.push(
        <g key={`arr-${t.id}-${pi}`}>
          <path d={d} fill="none" stroke="#94A3B8" strokeWidth={1.2} />
          <polygon
            points={`${x2 - 2},${y2 - 3} ${x2 + 3},${y2} ${x2 - 2},${y2 + 3}`}
            fill="#94A3B8"
          />
          {p.lag_days ? (
            <text x={(x1 + x2) / 2} y={Math.min(y1, y2) - 3} fontSize="9" fill="#64748B" textAnchor="middle">
              {p.lag_days > 0 ? `+${p.lag_days}d` : `${p.lag_days}d`}
            </text>
          ) : null}
        </g>,
      );
    });
  });

  return (
    <div className="relative flex-1 overflow-auto bg-white" data-testid="gantt-container">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: "block" }}
      >
        {/* Header background */}
        <rect x={0} y={0} width={width} height={HEADER_H} fill="#F8FAFC" />
        <line x1={0} y1={HEADER_H} x2={width} y2={HEADER_H} stroke="#E2E8F0" />
        {headerCells}
        {/* Row separator lines */}
        {tasks.map((_, i) => (
          <line
            key={`rl-${i}`}
            x1={0} x2={width}
            y1={HEADER_H + (i + 1) * ROW_H}
            y2={HEADER_H + (i + 1) * ROW_H}
            stroke="#F1F5F9"
          />
        ))}
        {/* Today marker */}
        {todayVisible ? (
          <g>
            <line x1={todayX} y1={HEADER_H} x2={todayX} y2={height} stroke="#F97316" strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={todayX + 3} y={HEADER_H - 4} fontSize="9" fill="#F97316" fontWeight="700">TODAY</text>
          </g>
        ) : null}
        {arrows}
        {rows}
        {/* Ghost dependency line while user drags from a connect handle */}
        {connect ? (
          <g pointerEvents="none">
            <line x1={connect.x1} y1={connect.y1} x2={connect.x2} y2={connect.y2}
              stroke="#387EE6" strokeWidth={1.5} strokeDasharray="4 3" />
            <circle cx={connect.x2} cy={connect.y2} r={3} fill="#387EE6" />
          </g>
        ) : null}
      </svg>
    </div>
  );
}
